import { ReactNode, Ref, useEffect, useState, useImperativeHandle, Fragment } from 'react';
import { Order } from '@anytag/graphql';
import { cn } from '@anytag/shared-utils';
import { useMeasure, useParamsSearch } from '@anytag/shared-hooks';
import {
  Row,
  Table as TRTable,
  ColumnDef,
  flexRender,
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel
} from '@tanstack/react-table';
import { Icon } from '../Icon';
import { NewTable } from '../shadcn-blocks';
import { ListEmpty, ListEmptyProps } from '../ListEmpty';
import { Pagination, PaginationProps } from '../pagination';
import { RenderDataWithFallback } from '../RenderDataWithFallback';
import { Tooltip } from '../Tooltip';
import { useTableState } from './hooks';
import { getCommonPinningStyles } from './utils';

export interface TableV2Props<TData, TValue = unknown> {
  id?: string;
  loading?: boolean;
  className?: string;
  maxHeight?: string;
  rowHeight?: string;
  hasBorder?: boolean;
  children?: ReactNode;
  data: readonly TData[];
  empty?: ListEmptyProps;
  stickyHeader?: boolean;
  skipNoDataNode?: boolean;
  ref?: Ref<HTMLDivElement>;
  hasLoadingOverlay?: boolean;
  noTopBorderRadius?: boolean;
  pagination?: PaginationProps;
  hasColumnCustomization?: boolean; // Toggle show/hide columns. By default all columns are visible, if hasColumnCustomization is true, all columns are hidden by default and some columns which have `enableHiding: false` will be set as default visible columns
  reactTableRef?: Ref<TRTable<TData>>;
  columns: ColumnDef<TData, TValue>[];
  customExpandedRow?: (row: Row<TData>) => ReactNode;
}

export const TableV2 = <TData, TValue>({
  id,
  ref,
  data,
  empty,
  columns,
  loading,
  children,
  hasBorder,
  className,
  rowHeight,
  pagination,
  stickyHeader,
  reactTableRef,
  skipNoDataNode,
  noTopBorderRadius,
  hasLoadingOverlay,
  maxHeight = '500px',
  hasColumnCustomization = false,
  customExpandedRow
}: TableV2Props<TData, TValue>) => {
  const [tableRef, tableDims] = useMeasure();
  const [wrapperRef, wrapperDims] = useMeasure();
  const [mounted, setMounted] = useState(false);
  const { sort, setSort } = useParamsSearch();
  const { state, setVisibleColumns } = useTableState({ columns, hasColumnCustomization });
  const table = useReactTable({
    state, // Using state instead of initialState to avoid the latest changes not updated
    columns,
    data: data as TData[],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setVisibleColumns,
    // By default, the row.getCanExpand() will return false unless it finds `subRows` on a row. And `subRows` will render same as normal rows
    // In case we want to custom UI for the expanded row, let use `customExpandedRow`
    // https://tanstack.com/table/v8/docs/guide/expanding
    getRowCanExpand: customExpandedRow ? () => true : undefined
  });
  // Expose table methods to parent component
  useImperativeHandle(reactTableRef, () => table, [table]);

  useEffect(() => {
    if (!loading) {
      setMounted(true);
    }
  }, [loading]);

  const hasData = !!data.length;
  const hasTableBorder = hasBorder ?? (!loading && hasData);
  const loadingTableFirstTime = loading && (!hasData || !mounted); // We want to make loading 1st time and keep table once it rendered and rerender the data only
  const loadingTableAfterMounted = loading && mounted;
  const isOverflow = !!(tableDims.width && wrapperDims.width && tableDims.width > wrapperDims.width);

  return (
    <div
      id={id}
      ref={ref}
      className={cn(
        'relative',
        {
          'pointer-events-none opacity-50 *:pointer-events-none': hasLoadingOverlay || loadingTableAfterMounted
        },
        className
      )}
    >
      <div
        ref={wrapperRef}
        style={{ maxHeight: stickyHeader ? maxHeight : 'none' }}
        className={cn('table-wrapper box-border w-full overflow-x-auto overflow-y-hidden rounded-b-sm bg-white', {
          'rounded-t-sm': !noTopBorderRadius,
          'border-t border-border': hasTableBorder
        })}
      >
        <RenderDataWithFallback
          loading={loadingTableFirstTime}
          noDataNode={<ListEmpty {...empty} />}
          hasNoData={!hasData && !loadingTableFirstTime && !skipNoDataNode}
          loadingProps={{ className: cn({ 'max-h-[inherit]': stickyHeader }) }}
        >
          <NewTable.Table ref={tableRef}>
            <NewTable.TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <NewTable.TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const { align, help, sortField } = header.column.columnDef.meta || {};
                    const { styles, isPinned, pinningClassName } = getCommonPinningStyles(header.column, isOverflow);
                    const sortOrder = !!sortField && sort.field === sortField ? sort.order : null;
                    const hasSort = data.length > 1 && header.column.getCanSort() && !!sortField;

                    return (
                      <NewTable.TableHead
                        style={styles}
                        key={header.id}
                        className={cn(
                          `text-${align || 'left'}`,
                          {
                            'pr-6!': hasSort,
                            'sticky top-0 z-2': stickyHeader,
                            'z-3!': isPinned && stickyHeader
                          },
                          pinningClassName
                        )}
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            <Tooltip help={help} place="top" withinPortal>
                              <span className={cn({ 'cursor-pointer hover:text-black-60': help })}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                            </Tooltip>
                            {hasSort ? (
                              <div className="absolute top-2 right-1 inline-flex flex-col align-middle">
                                <Icon
                                  size="8px"
                                  icon="triangle-up"
                                  className="box-content p-1"
                                  variant={sortOrder === Order.ASC ? 'blue' : 'gray'}
                                  onClick={() =>
                                    setSort(
                                      sortOrder === Order.ASC
                                        ? { field: null, order: null }
                                        : { field: sortField as string, order: Order.ASC }
                                    )
                                  }
                                />
                                <Icon
                                  size="8px"
                                  icon="triangle-down"
                                  className="box-content p-1"
                                  variant={sortOrder === Order.DESC ? 'blue' : 'gray'}
                                  onClick={() =>
                                    setSort(
                                      sortOrder === Order.DESC
                                        ? { field: null, order: null }
                                        : { field: sortField as string, order: Order.DESC }
                                    )
                                  }
                                />
                              </div>
                            ) : null}
                          </>
                        )}
                      </NewTable.TableHead>
                    );
                  })}
                </NewTable.TableRow>
              ))}
            </NewTable.TableHeader>
            <NewTable.TableBody style={rowHeight ? { ['--row-height' as string]: rowHeight } : undefined}>
              {table.getRowModel().rows?.length
                ? table.getRowModel().rows.map((row) => (
                    <Fragment key={row.id}>
                      {/* Normal row UI */}
                      <NewTable.TableRow
                        data-state={row.getIsSelected() && 'selected'}
                        className={(() => {
                          const original = row.original as any;

                          // Add custom row class name
                          return ('className' in original && original.className) || '';
                        })()}
                      >
                        {row.getVisibleCells().map((cell) => {
                          // We just support left pinning for now
                          const cellContextColumn = cell.getContext().column;
                          const { styles, pinningClassName } = getCommonPinningStyles(cellContextColumn, isOverflow);

                          return (
                            <NewTable.TableCell
                              key={cell.id}
                              style={styles}
                              className={cn(
                                `text-${cellContextColumn.columnDef.meta?.align || 'left'}`,
                                pinningClassName
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </NewTable.TableCell>
                          );
                        })}
                      </NewTable.TableRow>

                      {/* If the row is expanded and need to custom then we render a separate row with a single cell that spans the width of the table */}
                      {/* In case the expanded rows are the same as normal rows, let's use `subRows` instead */}
                      {/* Sample using `customExpandedRow`: useInfluencerPackagesTable.tsx */}
                      {/* Sample using `subRows`: https://tanstack.com/table/v8/docs/framework/react/examples/expanding */}
                      {row.getIsExpanded() && customExpandedRow ? (
                        <NewTable.TableRow data-state={row.getIsSelected() && 'selected'}>
                          <td colSpan={row.getAllCells().length}>{customExpandedRow(row)}</td>
                        </NewTable.TableRow>
                      ) : null}
                    </Fragment>
                  ))
                : null}
            </NewTable.TableBody>
          </NewTable.Table>
        </RenderDataWithFallback>
      </div>
      {children}
      {!loading && !!pagination ? <Pagination {...pagination} /> : null}
    </div>
  );
};
